import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/client';
import type { WorkOrderRecord } from '../work-orders/work-orders.types';
import type {
  ChangeApprovalRecord,
  ChangeHalf,
  ConfirmHalfBody,
  HalfKind,
  PlanHalfBody,
  ChangeTemplateOption,
  ClosureCode,
  FreezeWindowRecord,
  SiteChangeCreate,
  SiteChangeDetail,
  SiteChangeRecord,
  SiteChangeStatus,
  SiteChangeUpdate,
} from './site-changes.types';

const BASE = '/v1/itsm/changes';
const KEY = ['site-changes'] as const;

export function useSiteChanges(openOnly = false) {
  return useQuery({
    queryKey: [...KEY, { openOnly }],
    queryFn: () => api.get<SiteChangeRecord[]>(`${BASE}?open_only=${openOnly}`),
    refetchInterval: 30_000,
  });
}

/** Active change templates from the commons replica (R-133 — feeds the create drawer picker). */
export function useSiteChangeTemplates() {
  return useQuery({
    queryKey: [...KEY, 'templates'],
    queryFn: () => api.get<ChangeTemplateOption[]>(`${BASE}/templates`),
    staleTime: 300_000,
  });
}

export function useSiteChange(id: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => api.get<SiteChangeDetail>(`${BASE}/${id}`),
    enabled: !!id,
  });
}

export function useChangeCalendar(fromMs?: number, toMs?: number) {
  const params = new URLSearchParams();
  if (fromMs != null) params.set('from', String(fromMs));
  if (toMs != null) params.set('to', String(toMs));
  const qs = params.toString();
  return useQuery({
    queryKey: [...KEY, 'calendar', { fromMs, toMs }],
    queryFn: () => api.get<SiteChangeRecord[]>(`${BASE}/calendar${qs ? `?${qs}` : ''}`),
    refetchInterval: 60_000,
  });
}

export function useCreateSiteChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SiteChangeCreate) => api.post<SiteChangeRecord>(BASE, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateSiteChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: SiteChangeUpdate & { id: string }) =>
      api.post<SiteChangeRecord>(`${BASE}/${id}/update`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useTransitionSiteChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
      note,
      override_conflicts,
      closure_code,
    }: {
      id: string;
      /** ⚠ R-309: EVERY state is a legal target, `plan` included — going back to
       *  Plan IS the send-back, and it is what re-opens the fields. The server
       *  owns which from-states each target accepts. */
      status: SiteChangeStatus;
      note?: string;
      override_conflicts?: boolean;
      /** Required when status='closed' (R-142): complete | incomplete | unsuccessful. */
      closure_code?: ClosureCode;
    }) =>
      api.post<SiteChangeRecord>(`${BASE}/${id}/transition`, {
        status,
        note,
        override_conflicts,
        closure_code,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Record an ECAB verbal approval (R-142) — required before an emergency change
 * can move scheduled→implement. */
export function useEcabVerbal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, approved_by, note }: { id: string; approved_by: string; note?: string }) =>
      api.post<SiteChangeRecord>(`${BASE}/${id}/ecab-verbal`, { approved_by, note }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSetChangeApprovals() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, approvers }: { id: string; approvers: { approver_id: string; role?: string }[] }) =>
      api.post<ChangeApprovalRecord[]>(`${BASE}/${id}/approvals`, { approvers }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** R-309 — a decision on the change's SHARED approval chain.
 *
 * ⚠ NOT `useDecideSiteChange` below, which posts to `/decision` and answers the
 * ITIL **CAB panel** — dormant overlay machinery present only on a `requires_cab`
 * change. THIS posts to `/approval-decision` and answers the chain every change
 * gets at submit, built from `approval_rules` with `record_type = 'change'`.
 *
 * ⚠ THE OUTCOME MOVES THE RECORD, NOT THIS CALL. Satisfying the last level is what
 * takes a change to `approved` (and reserves the unit); a rejection sends it back to
 * an editable Plan. So this returns the CHANGE, not an approval row — the caller
 * should render what came back rather than assume what it did.
 */
export function useDecideChangeApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      decision,
      comments,
    }: {
      id: string;
      decision: 'approved' | 'rejected';
      comments?: string;
    }) => api.post<SiteChangeRecord>(`${BASE}/${id}/approval-decision`, { decision, comments }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDecideSiteChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, approve, note }: { id: string; approve: boolean; note?: string }) =>
      api.post<ChangeApprovalRecord>(`${BASE}/${id}/decision`, { approve, note }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** FX-2: append an ad-hoc CTASK to an open change (handbook H.2; UC-2 emergency
 * guarded sequence). Invalidates the change detail so the new task shows. */
export function useAddChangeTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      activity: string;
      note?: string;
      duration_min?: number;
      skill?: string;
    }) => api.post<WorkOrderRecord>(`${BASE}/${id}/tasks`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useCommentSiteChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      api.post<boolean>(`${BASE}/${id}/comment`, { note }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useFreezeWindows() {
  return useQuery({
    queryKey: [...KEY, 'freeze'],
    queryFn: () => api.get<FreezeWindowRecord[]>(`${BASE}/freeze-windows`),
  });
}

export function useCreateFreezeWindow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason: string; starts_at: number; ends_at: number }) =>
      api.post<FreezeWindowRecord>(`${BASE}/freeze-windows`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteFreezeWindow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<boolean>(`${BASE}/freeze-windows/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Cloud HQ read of the change fleet mirror (R-127; read-only per D-106). */
export function useFleetChanges(openOnly = true) {
  return useQuery({
    queryKey: [...KEY, 'fleet', { openOnly }],
    queryFn: () =>
      api.get<{ site_id: string; row: SiteChangeRecord; occurred_at_ms: number }[]>(
        `/users/v1/itsm/fleet/changes?open_only=${openOnly}`,
      ),
    refetchInterval: 30_000,
  });
}

// ─── R-238: the two halves of a change ───────────────────────────────────────

export function useChangeHalves(changeId: string | null | undefined) {
  return useQuery({
    queryKey: [...KEY, changeId, 'halves'],
    queryFn: () => api.get<ChangeHalf[]>(`${BASE}/${changeId}/halves`),
    enabled: !!changeId,
  });
}

/** Plan (or re-plan) one half. An UPSERT keyed on kind — the table's UNIQUE is what
 *  makes "at most one of each" true, so calling this twice edits rather than
 *  duplicates. Planning writes nothing to the register; the asset does not move
 *  until a technician confirms. */
export function usePlanChangeHalf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ changeId, kind, ...body }: { changeId: string; kind: HalfKind } & PlanHalfBody) =>
      api.put<ChangeHalf>(`${BASE}/${changeId}/halves/${kind}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Drop a planned half — an installation is a replacement whose removal half is
 *  simply absent. 409s once confirmed: that is a record of physical work. */
export function useRemoveChangeHalf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ changeId, kind }: { changeId: string; kind: HalfKind }) =>
      api.delete<boolean>(`${BASE}/${changeId}/halves/${kind}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** The technician's confirmation. THIS is what moves the asset — the register write
 *  and the confirmation are one transaction server-side, so a half can never read
 *  confirmed over a move that did not happen.
 *
 *  Invalidates the ASSET keys too: the unit this half moved is now in a different
 *  state, and the register screens are showing the old one. */
export function useConfirmChangeHalf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ halfId, ...body }: { halfId: string } & ConfirmHalfBody) =>
      api.post<ChangeHalf>(`${BASE}/halves/${halfId}/confirm`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ['site-assets'] });
    },
  });
}

/** Force-release the hold on the incoming unit — the change owner or an admin only,
 *  enforced server-side. Returns false when nothing was held, which is a no-op
 *  rather than a failure. */
export function useReleaseChangeReservation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (changeId: string) => api.post<boolean>(`${BASE}/${changeId}/release-reservation`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ['site-assets'] });
    },
  });
}

/** R-238 §6 C4 — the technician could not complete this half.
 *
 *  Returns which shape the failure took: `returned_to_plan` when nothing had been
 *  applied (hold released, chain wiped, halves dropped, both units untouched) or
 *  `closed_incomplete` when the other half already happened and therefore stands.
 *  The caller shows the difference, because "reported" alone does not tell the
 *  requester whether they are replanning or raising a new change.
 *
 *  Invalidates the ASSET keys as well as the change ones: a returned-to-plan
 *  failure releases a reservation, so the register screens are showing a unit that
 *  is no longer held. */
export function useFailChangeHalf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { halfId: string; reason: string }) =>
      api.post<string>(`${BASE}/halves/${v.halfId}/fail`, { reason: v.reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ['site-assets'] });
    },
  });
}
